import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useController } from 'react-hook-form';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import CustomIcon from '@/components/CustomIcon';
import { useStore } from '@/components/store/Store';
import { SettingsComponent } from '../../lib/declarations/EditorComponentTypes';
import { useCurrentBlock } from '../CurrentBlockProvider';
import { BlockSpecificPage } from '../../lib/declarations/BlockSpecificPage';
import ActionMenu from '../../../components/ActionMenu';
import { PageListBlock, PageListLayout, PageListViewType } from '../../../declarations/models/blocks/PageListBlocks';

const LAYOUT_OPTIONS_ICONS: Record<PageListLayout, ReactNode> = {
  [PageListLayout.IMAGE_TOP]: <CustomIcon name='banner_image_above' />,
  [PageListLayout.IMAGE_TOP_NO_MARGIN]: <MaterialSymbol name='horizontal_split' sx={{ transform: 'scaleY(-1)' }} />,
  [PageListLayout.TEXT_ON_IMAGE]: <MaterialSymbol name='article' />,
  [PageListLayout.TEXTLINES]: <MaterialSymbol name='text_ad' />,
};

export const PageBlockPageListLayoutSetting: SettingsComponent = () => {
  const { t: tAria } = useTranslation('aria');
  const { t: tComponents } = useTranslation('components');
  const { block, blockPath } = useCurrentBlock();
  const currentBlock = block as PageListBlock;
  const {
    state: { currentUser },
  } = useStore();
  const isSuperAdmin = currentUser?.user?.is_super_admin;
  const LAYOUT_OPTIONS: Array<PageListLayout> = isSuperAdmin
    ? Object.values(PageListLayout)
    : Object.values(PageListLayout).filter((layout) => layout !== PageListLayout.TEXTLINES);
  const {
    field: { value, onChange },
  } = useController<BlockSpecificPage<PageListBlock>, `${typeof blockPath}.layout`>({
    name: `${blockPath}.layout`,
  });

  return (
    <ActionMenu
      id='page-list-layout-selector'
      ariaLabel={tAria('components.PageBlockPageListLayoutSetting.ActionMenuLabel')}
      tooltip={tComponents('PageBlockPageListLayoutSetting.Tooltip')}
      icon={value ? LAYOUT_OPTIONS_ICONS[value as PageListLayout] : LAYOUT_OPTIONS_ICONS['img-top']}
      disabled={currentBlock.view === PageListViewType.LIST || currentBlock.view === PageListViewType.SIMPLE}>
      {LAYOUT_OPTIONS.map((layoutOption) => (
        <MenuItem
          key={layoutOption}
          onClick={() => onChange(layoutOption)}
          selected={value === layoutOption}
          aria-label={tComponents(`PageBlockPageListLayoutSetting.options.${layoutOption}`)}>
          <ListItemIcon>{LAYOUT_OPTIONS_ICONS[layoutOption]}</ListItemIcon>
          <ListItemText>{tComponents(`PageBlockPageListLayoutSetting.options.${layoutOption}`)}</ListItemText>
        </MenuItem>
      ))}
    </ActionMenu>
  );
};
